/*
 * Build-aware equipment strategies.
 *
 * Package scenarios describe what an Item + two Add-ons can do in isolation.
 * These strategies describe what the complete perk deck changes: recharge
 * loops, staged Item routes and exact execution payoffs.
 */
export const SURVIVOR_EQUIPMENT_BUILD_STRATEGIES = {
  built_to_last_capacity_cycle: {
    labels: {
      en: 'Built to Last: maximum-charge cycle',
      fr: 'Conçu pour durer : cycle de charges maximales'
    },
    descriptions: {
      en: 'Wire Spool and Scraps raise the Toolbox maximum to 52 Charges. Built to Last then restores 99%, 66% and 33% of that larger maximum, for 154.96 projected Charges across the Trial.',
      fr: 'La Bobine de fil et la Ferraille portent la caisse à 52 charges. Conçu pour durer recharge ensuite 99 %, 66 % puis 33 % de ce maximum augmenté, soit 154,96 charges projetées sur la partie.'
    },
    conditions: {
      en: 'A Brand New Part is consumed once and is not restored by Built to Last, so it loses to two capacity Add-ons in this specific loop.',
      fr: 'La Pièce flambant neuve est consommée une seule fois et ne revient pas avec Conçu pour durer : elle perd donc face aux deux add-ons de capacité dans cette boucle précise.'
    }
  },
  scavenger_capacity_cycle: {
    labels: {
      en: 'Scavenger: full-Toolbox recharge loop',
      fr: 'Récupérateur : boucle de recharge complète'
    },
    descriptions: {
      en: 'The package maximises the Charges returned by each Scavenger recharge instead of spending an Add-on slot on a one-use effect.',
      fr: 'Le package maximise les charges rendues par chaque recharge de Récupérateur au lieu de dépenser un slot d’add-on dans un effet à usage unique.'
    },
    conditions: {
      en: 'The loop still requires five Great Repair Skill Checks and applies Scavenger’s Repair-speed penalty after each recharge.',
      fr: 'La boucle demande toujours cinq excellents skill checks de réparation et applique le malus de vitesse de Récupérateur après chaque recharge.'
    }
  },
  residual_flashbang_pipeline: {
    labels: {
      en: 'Two-item route: Toolbox → Flashbang → Flashlight',
      fr: 'Route double item : caisse → grenade → lampe'
    },
    descriptions: {
      en: 'Without an Item recharge perk, Brand New Part removes 10 permanent Generator Charges and Wire Spool leaves enough Toolbox capacity to cross Flashbang’s 40% personal Repair threshold. Residual Manifest then guarantees a Basic Flashlight from an opened Chest.',
      fr: 'Sans perk de recharge d’objet, la Pièce flambant neuve retire 10 charges permanentes au générateur et la Bobine de fil laisse assez de capacité pour franchir les 40 % de réparation personnelle de Grenade aveuglante. Manifeste résiduel garantit ensuite une lampe basique dans un coffre ouvert.'
    },
    conditions: {
      en: 'With Built to Last or Scavenger, Wire Spool plus Scraps becomes preferable because recharges multiply maximum capacity but cannot restore the consumed Brand New Part. The route still needs access to an opened Chest.',
      fr: 'Avec Conçu pour durer ou Récupérateur, Bobine de fil plus Ferraille redevient préférable : les recharges multiplient la capacité maximale mais ne restaurent pas la Pièce flambant neuve consommée. La route demande toujours l’accès à un coffre ouvert.'
    }
  },
  brand_new_part_opener: {
    labels: {
      en: 'Brand New Part: front-loaded objective swing',
      fr: 'Pièce flambant neuve : impact immédiat sur l’objectif'
    },
    descriptions: {
      en: 'Brand New Part permanently removes 10 Charges from one Generator’s requirement, then the remaining Toolbox capacity is delivered normally. It is prioritised for immediate Generator pressure when no recharge perk can multiply a second capacity Add-on.',
      fr: 'La Pièce flambant neuve retire définitivement 10 charges au besoin d’un générateur, puis la capacité restante de la caisse est transférée normalement. Elle est priorisée pour une pression générateur immédiate lorsqu’aucune perk de recharge ne peut multiplier un second add-on de capacité.'
    },
    conditions: {
      en: 'The difficult Skill Check and one-use consumption make it weaker in Built to Last and Scavenger loops.',
      fr: 'Le skill check difficile et la consommation à usage unique la rendent moins forte dans les boucles Conçu pour durer et Récupérateur.'
    }
  },
  flashlight_chase_control: {
    labels: {
      en: 'Chase blind: reach and repeat attempts',
      fr: 'Aveuglement en chase : portée et tentatives'
    },
    descriptions: {
      en: 'Utility Flashlight supplies the useful battery and base Blind duration. High-End Sapphire Lens extends reach and the successful Blind window; Long Life Battery preserves repeated pallet and animation-lock attempts.',
      fr: 'La Lampe utilitaire fournit l’autonomie utile et une meilleure durée de base. La Lentille en saphir haut de gamme augmente la portée et la fenêtre d’aveuglement ; la Pile longue durée conserve plusieurs tentatives sur palettes et animations verrouillées.'
    },
    conditions: {
      en: 'This is the chase package. Sport Flashlight plus Wide Lens and a Grip is an aiming-comfort package and belongs to pickup saves, not to the chase-power ceiling.',
      fr: 'C’est le package de chase. Lampe de sport avec Lentille grand-angle et poignée est un package de confort de visée destiné aux flash saves, pas le plafond de puissance en chase.'
    }
  },
  flashlight_pickup_rescue: {
    labels: {
      en: 'Wide-angle pickup rescue',
      fr: 'Flash save à angle élargi'
    },
    descriptions: {
      en: 'Sport Flashlight supplies the minimum aim stability, Wide Lens opens side rescue angles, and Long Life Battery preserves enough attempts for pickup windows.',
      fr: 'La Lampe de sport fournit la stabilité minimale, la Lentille grand-angle ouvre les angles latéraux et la Pile longue durée conserve assez de tentatives sur les ramassages.'
    },
    conditions: {
      en: 'Range and Blind duration are intentionally secondary: neither restores an angle lost when the Killer faces a wall or turns away.',
      fr: 'Portée et durée d’aveuglement restent volontairement secondaires : aucune ne récupère un angle perdu quand le tueur fait face au mur ou se retourne.'
    }
  },
  flashlight_long_blind: {
    labels: {
      en: 'Extended blind reposition',
      fr: 'Repositionnement sous aveuglement prolongé'
    },
    descriptions: {
      en: 'Utility Flashlight and duration Add-ons extend the successful Blind window, creating more time to change direction or disappear after the hit.',
      fr: 'La Lampe utilitaire et les add-ons de durée prolongent la fenêtre d’aveuglement réussie afin de laisser davantage de temps pour changer de direction ou disparaître.'
    },
    conditions: {
      en: 'Brightness itself has no score: only Blind duration and the battery trade-off matter.',
      fr: 'La luminosité elle-même vaut zéro dans le score : seules la durée d’aveuglement et la contrepartie d’autonomie comptent.'
    }
  },
  flashlight_endurance_loop: {
    labels: {
      en: 'Maximum usable beam time',
      fr: 'Autonomie réelle maximale'
    },
    descriptions: {
      en: 'Long Life Battery raises the maximum Battery and Low Amp Filament reduces depletion. Recharge perks multiply the resulting usable beam economy across repeated attempts.',
      fr: 'La Pile longue durée augmente la batterie maximale et le Filament à basse consommation ralentit son épuisement. Les perks de recharge multiplient ensuite cette autonomie réelle sur plusieurs tentatives.'
    },
    conditions: {
      en: 'This package maximises total attempts; it does not make one individual blind easier to aim.',
      fr: 'Ce package maximise le nombre total de tentatives ; il ne rend pas un aveuglement isolé plus facile à viser.'
    }
  },
  key_team_locator: {
    labels: {
      en: 'Key route: locate the teammate who activates the deck',
      fr: 'Route clé : retrouver l’allié qui active le deck'
    },
    descriptions: {
      en: 'Skeleton Key, Braided Bauble and Friendship Charm create long, repeatable Survivor reads for a deck whose proximity effects first need a teammate to be found.',
      fr: 'La Clé passe-partout, la Babiole tressée et le Talisman d’amitié créent des lectures d’alliés longues et répétables pour un deck dont les effets de proximité exigent d’abord de retrouver un partenaire.'
    },
    conditions: {
      en: 'A generic Aura perk is not enough. The deck must contain both a real proximity activation and a second payoff for meeting or staying with that teammate.',
      fr: 'Une perk d’aura générique ne suffit pas. Le deck doit réunir une vraie activation de proximité et un second payoff qui récompense le regroupement ou le maintien avec cet allié.'
    }
  },
  key_killer_read: {
    labels: {
      en: 'Blood Amber: global, reciprocal Killer read',
      fr: 'Ambre de sang : lecture globale et réciproque du tueur'
    },
    descriptions: {
      en: 'Blood Amber removes the distance limit and Braided Bauble restores two seconds of the lost read. Eyes of Belmont extends the resulting timed Killer Aura by another two seconds.',
      fr: 'L’Ambre de sang supprime la limite de distance et la Babiole tressée restaure deux secondes de lecture. Les Yeux de Belmont prolongent encore de deux secondes l’aura chronométrée du tueur.'
    },
    conditions: {
      en: 'This is an on-demand information trade, not Stealth: while the Key reveals the Killer, the Killer sees the user too.',
      fr: 'C’est un échange d’information à la demande, pas de la furtivité : pendant que la clé révèle le tueur, le tueur voit aussi son utilisateur.'
    }
  },
  key_chest_route: {
    labels: {
      en: 'Key route: locked-Chest conversion',
      fr: 'Route clé : conversion de coffre verrouillé'
    },
    descriptions: {
      en: 'Skeleton Key, Shrill Whistle and Friendship Charm open a locked Chest quickly, guarantee a Rare-or-better Item and preserve the Key’s late-game option.',
      fr: 'La Clé passe-partout, le Sifflet strident et le Talisman d’amitié ouvrent rapidement un coffre verrouillé, garantissent un objet Rare ou meilleur et conservent l’option de fin de partie.'
    },
    conditions: {
      en: 'The deck must contain at least two real Chest, rummage or loot-conversion mechanics; generic Item economy does not qualify.',
      fr: 'Le deck doit contenir au moins deux vraies mécaniques de coffre, de fouille ou de conversion du loot ; l’économie d’item générique ne suffit pas.'
    }
  },
  key_hatch_escape: {
    labels: {
      en: 'Key route: closed-Hatch contingency',
      fr: 'Route clé : recours sur trappe refermée'
    },
    descriptions: {
      en: 'The Dull or Skeleton Key can reopen a Hatch closed by the Killer. Shrill Whistle shortens the exposed channel while Friendship Charm avoids sacrificing the Key’s other uses.',
      fr: 'La Clé terne ou passe-partout peut rouvrir une trappe fermée par le tueur. Le Sifflet strident raccourcit l’action exposée et le Talisman d’amitié préserve les autres usages de la clé.'
    },
    conditions: {
      en: 'Generic Endgame perks do not justify a Key. The deck must explicitly commit to a last-Survivor or Hatch fallback.',
      fr: 'Des perks d’endgame génériques ne justifient pas une clé. Le deck doit s’engager explicitement dans un recours de dernier survivant ou de trappe.'
    }
  },
  key_obsession_link: {
    labels: {
      en: 'Wedding Ring: permanent Obsession link',
      fr: 'Alliance : lien permanent avec l’Obsession'
    },
    descriptions: {
      en: 'Unique Wedding Ring creates a passive, permanent two-way Aura link with the Obsession while the Key remains available for active teammate reads.',
      fr: 'L’Alliance unique crée un lien d’aura passif, permanent et réciproque avec l’Obsession pendant que la clé reste disponible pour les lectures actives d’alliés.'
    },
    conditions: {
      en: 'This route requires an Obsession-targeted teammate mechanic plus a second linked-player payoff; a generic Obsession or Aura tag is insufficient.',
      fr: 'Cette route exige une mécanique d’allié ciblant l’Obsession et un second payoff de joueur lié ; un tag générique Obsession ou Aura ne suffit pas.'
    }
  },
  fog_prerun_disappearance: {
    labels: {
      en: 'Pre-run disappearance screen',
      fr: 'Écran de disparition en pré-run'
    },
    descriptions: {
      en: 'After line of sight is broken, Mushroom Formula and Oily Sap create a 10-metre, 14-second outside-in wall. Scratch-Mark and noise-denial perks then hide which edge the Survivor used to rotate.',
      fr: 'Après la rupture de ligne de vue, Formule fongique et Sève huileuse créent un mur extérieur de 10 m pendant 14 s. Les perks qui suppriment traces et bruits masquent ensuite le bord emprunté pour la rotation.'
    },
    conditions: {
      en: 'This requires at least two real disappearance mechanics. It is not selected for generic Stealth, anti-slug or chase builds, and it does not assume the Killer loses sight while sharing the cloud.',
      fr: 'Cette route exige au moins deux vraies mécaniques de disparition. Elle n’est pas choisie pour un build générique de furtivité, anti-slug ou chase, et ne suppose pas que le tueur perde la cible quand il partage le nuage.'
    }
  },
  anti_slug_medical_reset: {
    labels: {
      en: 'Anti-slug medical reset',
      fr: 'Reset médical anti-slug'
    },
    descriptions: {
      en: 'The Med-Kit reinforces repeated Dying-State pickups and follow-up heals, supporting the whole anti-slug deck instead of adding an unrelated rescue tool.',
      fr: 'Le kit médical renforce les relevages répétés depuis l’état agonisant et les soins qui suivent : il soutient tout le deck anti-slug au lieu d’ajouter un outil de sauvetage sans lien direct.'
    },
    conditions: {
      en: 'For the People can handle one instant transfer; the Med-Kit preserves value for the other resets and ordinary heals.',
      fr: 'Pour le peuple peut gérer un transfert instantané ; le kit conserve sa valeur pour les autres resets et les soins ordinaires.'
    }
  }
}

export function survivorEquipmentBuildStrategy (strategyId) {
  return SURVIVOR_EQUIPMENT_BUILD_STRATEGIES[strategyId] || null
}
